import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    getLeaderboard,
    fetchUserJoinedSquads,
    fetchTournaments
} from "../api/api.js";
import countries from "../data/countries";
import tournamentIcons from '../data/tournamentIcons.js';
import {
    getTournamentData,
    buildTournamentMap
} from "../utils/tournamentUtils";

function Leaderboard() {
    const [tournaments, setTournaments] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: "score", direction: "desc" });
    const [selectedFilter, setSelectedFilter] = useState("all");
    const [joinedSquads, setJoinedSquads] = useState([]);
    const [filterOptions, setFilterOptions] = useState([
        { id: "all", name: "All" }
    ]);
    const [seasons, setSeasons] = useState([]);
    const [selectedSeason, setSelectedSeason] = useState("");

    const seasonOptions = [
        ...seasons,
        {
            id: "all",
            name: "All seasons"
        }
    ];

    const loggedUser = JSON.parse(localStorage.getItem("user"));

    const buildSquadArenaOptions = (joinedSquads) => {
        const visibleTournamentIds =
            getVisibleTournamentIds();

        const options = [];

        joinedSquads.forEach((squad) => {
            squad.arenas.forEach((arena) => {
                if (!arena.userJoined) {
                    return;
                }

                const hasVisibleTournament =
                    arena.enabledTournaments.some(
                        t => visibleTournamentIds.includes(t.id)
                    );

                if (!hasVisibleTournament) {
                    return;
                }

                const tournamentNames = arena.enabledTournaments
                    .map((tournament) => {

                        const tournamentData =
                            tournamentMap[tournament.id];

                        if (!tournamentData) {
                            return null;
                        }

                        return tournamentIcons[tournamentData.name]
                            || tournamentData.name;
                    })
                    .filter(Boolean)
                    .join(" ");

                options.push({
                    id: `arena:${arena.id}`,
                    name: `${squad.name} ${tournamentNames}`
                });
            });
        });

        return options;
    };

    const buildFilterOptions = (joinedSquads = null) => {
        const squadArenaOptions = joinedSquads ? buildSquadArenaOptions(joinedSquads) : [];
        const visibleTournaments = getVisibleTournaments();

        const options = []
        options.push({ id: "all", name: "All" });

        if (squadArenaOptions.length > 0) {
            options.push({
                id: "disabled-squads",
                name: "-- Squads --"
            });

            options.push(...squadArenaOptions);
        }

        if (visibleTournaments.length > 0) {
            options.push({
                id: "disabled-tournaments",
                name: "-- Tournaments --"
            });

            options.push(
                ...visibleTournaments.map((tournament) => ({
                    id: `tournament:${tournament.id}`,
                    name: `${tournamentIcons[tournament.name] || "⚽"} ${tournament.name}`
                }))
            );
        }

        return options;
    };

    const tournamentMap = buildTournamentMap(tournaments);

    const loadLeaderboard = async (season = "all", filter = "all", sortKey = "score", sortDirection = "desc") => {
        setLoading(true);
        setError(null);

        try {
            const data = await getLeaderboard(season, filter, sortKey, sortDirection);
            setLeaderboard(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadFilterOptions = async () => {
        if (loggedUser) {
            try {
                const response = await fetchUserJoinedSquads();
                const squads = response.data || [];

                setJoinedSquads(squads);

                const options = buildFilterOptions(squads);
                setFilterOptions(options);

            } catch (err) {
                console.error("Failed to load squads for filter", err);
                
                const options = buildFilterOptions();
                setFilterOptions(options);
            }
        } else {
            const options = buildFilterOptions();
            setFilterOptions(options);
        }
    };

    const getVisibleTournamentIds = () => {
        if (selectedSeason === "all") {
            return [];
        }

        const season = seasons.find(
            s => String(s.id) === String(selectedSeason)
        );

        return season?.tournaments ?? [];
    };

    const getVisibleTournaments = () => {
        const ids = getVisibleTournamentIds();

        return tournaments.filter(
            t => ids.includes(t.id)
        );
    };

    const getSavedFilters = () => {
        try {
            return JSON.parse(
                localStorage.getItem("leaderboard-filter")
            ) || {};
        } catch {
            return {};
        }
    };

    const saveFilterForSeason = (seasonId, filter) => {
        if (!seasonId) {
            return;
        }

        const filters = getSavedFilters();

        filters[seasonId] = filter;

        localStorage.setItem(
            "leaderboard-filter",
            JSON.stringify(filters)
        );
    };

    useEffect(() => {
        const initialize = async () => {
            try {
                const {
                    tournaments,
                    seasons
                } = await getTournamentData(fetchTournaments);

                setTournaments(tournaments);
                setSeasons(seasons);

                const liveSeason = seasons.find(
                    s => s.status === "live"
                );

                setSelectedSeason(
                    liveSeason
                        ? String(liveSeason.id)
                        : "all"
                );

            } catch (err) {
                console.error(
                    "Failed to load tournaments",
                    err
                );
            }
        };

        initialize();
    }, []);

    useEffect(() => {
        if (tournaments.length === 0) {
            return;
        }

        loadFilterOptions();

    }, [
        tournaments,
        selectedSeason
    ]);

    useEffect(() => {
        loadLeaderboard(selectedSeason, selectedFilter, sortConfig.key, sortConfig.direction);
    }, [selectedSeason, selectedFilter, sortConfig]);

    useEffect(() => {
        const options =
            buildFilterOptions(
                joinedSquads
            );

        const exists =
            options.some(
                option =>
                    option.id === selectedFilter
            );

        if (!exists) {
            setSelectedFilter("all");
        }

    }, [
        selectedSeason,
        tournaments
    ]);

    useEffect(() => {
        const exists = filterOptions.some(
            option => option.id === selectedFilter
        );

        if (exists) {
            return;
        }

        const firstSelectable = filterOptions.find(
            option => !option.id.startsWith("disabled")
        );

        setSelectedFilter(
            firstSelectable?.id ?? ""
        );
    }, [filterOptions]);

    const handleSort = (key) => {
        const newDirection = sortConfig.key === key && sortConfig.direction === "desc" ? "asc" : "desc";
        setSortConfig({ key, direction: newDirection });
    };

    const handleSeasonChange = (seasonId) => {
        setSelectedSeason(seasonId);

        const filters = getSavedFilters();

        setSelectedFilter(filters[seasonId] || "all");
    };

    const onFilterChange = (value) => {
        setSelectedFilter(value);
        saveFilterForSeason(selectedSeason, value);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="content-container">
            <div className="filter-dropdown-container">
                <div className="filter-dropdown-group">

                    <select
                        value={selectedSeason}
                        onChange={(e) =>
                            handleSeasonChange(
                                e.target.value
                            )
                        }
                        className="filter-dropdown leaderboard-dropdown"
                    >
                        {seasonOptions.map((season) => (
                            <option
                                key={season.id}
                                value={season.id}
                            >
                                {season.name}
                            </option>
                        ))}
                    </select>

                    <select
                        value={selectedFilter}
                        onChange={(e) =>
                            onFilterChange(
                                e.target.value
                            )
                        }
                        className="filter-dropdown leaderboard-dropdown"
                    >
                        {filterOptions.map((option) => (
                            <option
                                key={option.id}
                                value={option.id}
                                disabled={option.id.startsWith("disabled")}
                            >
                                {option.name}
                            </option>
                        ))}
                    </select>

                </div>

            </div>

            <div className="leaderboard dark-main-container">
                <div className='table-wrapper'>
                    <table>
                        <thead>
                            <tr>
                                <th className="col-rank">#</th>
                                <th onClick={() => handleSort("username")} className="left col-user">
                                    User <span className={`sort-icon ${sortConfig.key === "username" ? sortConfig.direction : ""}`} />
                                </th>
                                <th onClick={() => handleSort("score")} className="score col-score">
                                    Score <span className={`sort-icon ${sortConfig.key === "score" ? sortConfig.direction : ""}`} />
                                </th>
                                <th onClick={() => handleSort("perfect_predictions")} className="score col-perfects">
                                    Perfects <span className={`sort-icon ${sortConfig.key === "perfect_predictions" ? sortConfig.direction : ""}`} />
                                </th>
                                <th onClick={() => handleSort("avg")} className="score col-avg">
                                    Average <span className={`sort-icon ${sortConfig.key === "avg" ? sortConfig.direction : ""}`} />
                                </th>
                                <th onClick={() => handleSort("total_predictions")} className="score col-predictions">
                                    Predictions <span className={`sort-icon ${sortConfig.key === "total_predictions" ? sortConfig.direction : ""}`} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboard.length > 0 ? (
                                leaderboard.map((user, index) => {
                                    return (
                                        <tr key={user.user_id}>
                                            <td className="highlight col-rank">{index + 1}</td>
                                            <td className={loggedUser && (loggedUser.username === user.username) ? "highlight col-user" : "col-user"}>
                                                <div className="user-info">
                                                    <img
                                                        src={user.country_img}
                                                        alt={user.country}
                                                        title={user.country}
                                                        className="country-flag"
                                                    />
                                                    <Link to={`/points/user/${user.username}`}>
                                                        {user.username}
                                                    </Link>
                                                </div>
                                            </td>
                                            <td className="highlight score col-score">{user.score}</td>
                                            <td className={loggedUser && (loggedUser.username === user.username) ? "highlight score col-perfects" : "score col-perfects"}>{user.perfect_predictions}</td>
                                            <td className={loggedUser && (loggedUser.username === user.username) ? "highlight score col-avg" : "score col-avg"}>{parseFloat(user.avg).toFixed(2)}</td>
                                            <td className={loggedUser && (loggedUser.username === user.username) ? "highlight score col-predictions" : "score col-predictions"}>{user.total_predictions}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="6">No leaderboard available.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default Leaderboard;
